import { useCallback, useContext, useState } from 'react';

import { Logger } from '@/utils/logger';
import { PreferencesContext } from '@montugroup/circuit-shared/context/Preferences';
import { PaymentService } from '@montugroup/circuit-shared/services/payment.service';

import type { DeliveryOption } from '@/hooks/order/types';
import { getAdditionalTemporalInfo } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { getSnowplowDuid } from '@montugroup/data-collection';
import { DateTime } from 'luxon';
import type { PersistedProductDetails } from './persistedOrderDetails';

const logger = new Logger('useShopifyCheckout');
const client = getTsCustomerPortalClient();

export type CheckoutDeliveryOption = DeliveryOption & {
  pharmacyId: number;
  displayName: string;
};

const useShopifyCheckout = ({
  orderId,
  orderCode,
  selectedProducts,
}: {
  orderId?: number;
  orderCode?: string;
  selectedProducts: PersistedProductDetails[] | undefined;
}) => {
  const { data: user } = useUser();
  const { mutateAsync, isPending } = client.createCheckout.useMutation();
  const [isNavigating, setIsNavigating] = useState(false);
  const { umedsMarketingConsent } = useContext(PreferencesContext);

  const placeOrderShopify = useCallback(
    async ({ deliveryOption }: { deliveryOption: CheckoutDeliveryOption }) => {
      try {
        if (!selectedProducts || selectedProducts.length === 0) {
          logger.error('No products selected');
          throw new Error('No products selected for checkout.');
        }

        if (!deliveryOption) {
          logger.error('No deliveryOption selected');
          throw new Error('Please select a delivery option.');
        }

        const productsPayload = selectedProducts.map(({ quantity, id }) => ({
          quantity,
          productId: id,
        }));

        PaymentService.clearOrderCode();
        PaymentService.clearUsedDeliveryOption();
        const snowplowIds = getSnowplowDuid();

        const {
          body: { data },
        } = await mutateAsync({
          query: {},
          body: {
            data: {
              deliveryOption: {
                ...deliveryOption,
                ...(deliveryOption.inventoryReservationId
                  ? {
                      cartReservationGroupToken: deliveryOption.inventoryReservationId,
                    }
                  : // NOTE: This `as` cast is because we will eventually transition from returning inventoryReservationId to returning cartReservationGroupToken, but in the latter case it will already be added via the `...deliveryOption` line
                    ({} as { cartReservationGroupToken: string })),
              },
              orderId: orderId || undefined,
              orderCode: orderCode || undefined,
              products: productsPayload,
              uMedsMarketingOptIn: umedsMarketingConsent,
              ...(snowplowIds && {
                snowplow: {
                  domainUserId: snowplowIds.domain_userid,
                  domainSessionIdx: snowplowIds.domain_sessionidx,
                  domainSessionId: snowplowIds.domain_sessionid,
                },
              }),
            },
          },
        });

        const additionalTemporalLogs = {
          browserTime: {
            ...getAdditionalTemporalInfo(DateTime.now().toISO()),
          },
          validBefore: {
            ...getAdditionalTemporalInfo(deliveryOption.validBefore),
          },
          ...('cutoffTime' in deliveryOption
            ? {
                cutoffTime: {
                  ...getAdditionalTemporalInfo(deliveryOption.cutoffTime),
                },
              }
            : {}),
          ...('deliveryWindow' in deliveryOption
            ? {
                deliveryWindowStartTime: {
                  ...getAdditionalTemporalInfo(deliveryOption.deliveryWindow.startTime),
                },
                deliveryWindowEndTime: {
                  ...getAdditionalTemporalInfo(deliveryOption.deliveryWindow.endTime),
                },
              }
            : {}),
        };

        const logData = {
          priorOrderCode: orderId || undefined,
          priorOrderId: orderCode || undefined,
          data,
          deliveryOption,
          snowplowIds,
          products: productsPayload,
          userConnections: user?.connections,
          additionalTemporalLogs,
        };

        if (data.orderCode) {
          PaymentService.setOrderCode(data.orderCode);
          PaymentService.setUsedDeliveryOption(deliveryOption.name);
        }

        if (data.checkoutRedirectUrl) {
          logger.info('redirecting to shopify checkout', logData);
          setIsNavigating(true);
          window.location.replace(data.checkoutRedirectUrl);
        } else {
          logger.error('Failed to retrieve checkoutUrl', logData);
          throw new Error('Something went wrong, please try again');
        }
      } catch (e) {
        logger.error('createCheckout request failed', { deliveryOption }, e);
        setIsNavigating(false);
        throw e;
      }
    },
    [mutateAsync, orderCode, orderId, selectedProducts, umedsMarketingConsent, user?.connections],
  );

  return {
    isSubmitting: isPending || isNavigating,
    placeOrderShopify,
  };
};

export default useShopifyCheckout;
